import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Check, ChevronsUpDown, X } from '../ui/icons';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '../ui/popover';
import { cn } from '../lib/utils';
import { useElementValue } from '../DocumentView/DocumentContext';
import { useDocument, useIsReadOnly } from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';
import { DOCUMENT_RESPONSE_SET_QUERY } from './queries';
import { FieldLabel, FieldSubLabel, FieldHelpText } from './FieldParts';

/**
 * Sentinel persisted when the filler picks "Other" and no fixed
 * `userEntryValue` is configured. Namespaced so it can't collide with
 * a real row value.
 */
const USER_ENTRY_SENTINEL = '__user_entry__';

interface Props {
  element: DocumentElementType;
}

interface SelectOption {
  value: string;
  label: string;
  /**
   * Extra lines shown beneath the main label. When `showName` is true
   * the line renders as "Column name: cell value"; otherwise it's the
   * bare cell value. Sourced from per-column toggles on TABLE sets.
   */
  extras?: Array<{ name: string; value: string; showName: boolean }>;
  /** Full row snapshot — present when sourced from a TABLE response set. */
  row?: Record<string, unknown>;
  /** Row's score under the set's scoring config, when scoring is on. */
  rowScore?: number | null;
  /** Row's flagged flag under the set's flagged column, when set. */
  rowFlagged?: boolean | null;
}

/**
 * Persisted shape for each picked answer. We snapshot the row data
 * (and its scoring metadata) at submission time so the answer keeps
 * displaying / scoring even if the source row is later removed or
 * renamed in the response set.
 *
 * User entries (no matching row) get `row: null` and `other: true`,
 * and inherit the set's `userEntryScore` / `userEntryFlagged` config.
 */
interface DropdownRichEntry {
  label: string;
  value: string;
  row: Record<string, unknown> | null;
  score: number | null;
  flagged: boolean | null;
  /**
   * True when this entry came from the "Create '<text>'" affordance
   * (a user-typed answer not present in the response set), false for
   * row picks. Downstream reporting can split row picks from custom
   * answers without re-deriving from `row === null`.
   */
  other: boolean;
}

const stringToRichEntry = (s: string): DropdownRichEntry => ({
  label: s,
  value: s,
  row: null,
  score: null,
  flagged: null,
  other: false,
});

// The mobile app persists an "Other" (user-entry) pick with a sentinel
// value — `__OTHER__` (single) or `__OTHER__:<text>` (multi) — and the
// free text in a string `other` field, whereas the web canonical shape
// is `{ value: <text>, other: true }`. Recognise the mobile sentinels so
// the two align.
const OTHER_SENTINEL = '__OTHER__';
const OTHER_SENTINEL_PREFIX = '__OTHER__:';

/**
 * Coerce one persisted entry into the canonical rich shape. Handles:
 *   - web entries (already `{ value, label, row, score, flagged, other }`)
 *   - the mobile "Other" shape (sentinel value + string `other` payload)
 *   - the mobile lean pick `{ value, label }` (fills missing metadata)
 * so answers written on either platform read back identically here.
 */
const normalizeEntry = (entry: unknown): DropdownRichEntry => {
  if (typeof entry === 'string') return stringToRichEntry(entry);
  if (!entry || typeof entry !== 'object') return stringToRichEntry('');
  const o = entry as Record<string, unknown>;
  const value = typeof o.value === 'string' ? o.value : '';
  const label = typeof o.label === 'string' ? o.label : value;
  const isMobileOther =
    value === OTHER_SENTINEL ||
    value.startsWith(OTHER_SENTINEL_PREFIX) ||
    typeof o.other === 'string';
  if (isMobileOther) {
    const text =
      typeof o.other === 'string' && o.other !== ''
        ? o.other
        : value.startsWith(OTHER_SENTINEL_PREFIX)
        ? value.slice(OTHER_SENTINEL_PREFIX.length)
        : label;
    return {
      value: text,
      label: label || text,
      row: null,
      score: null,
      flagged: null,
      other: true,
    };
  }
  return {
    value,
    label,
    row:
      o.row && typeof o.row === 'object'
        ? (o.row as Record<string, unknown>)
        : null,
    score: typeof o.score === 'number' ? o.score : null,
    flagged: typeof o.flagged === 'boolean' ? o.flagged : null,
    other: o.other === true,
  };
};

/**
 * Coerce whatever shape sits in the persisted answer into a stable
 * array of rich entries. Tolerates the pre-rich-row shapes so saved
 * draft answers keep loading cleanly:
 *   - bare string (legacy single-mode)
 *   - string[] (legacy multi-mode)
 *   - the new DropdownRichEntry[] (web or mobile)
 */
const toRichEntries = (raw: unknown): DropdownRichEntry[] => {
  if (Array.isArray(raw)) return raw.map(normalizeEntry);
  if (typeof raw === 'string' && raw !== '') return [stringToRichEntry(raw)];
  return [];
};

// Truthy interpretation of the flagged column's cell value — matches
// what a designer would expect from "yes" / "true" / "1" / "x".
const isTruthyCell = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '0' && s !== 'false' && s !== 'no';
};

const isLocalResponseSetId = (id: string | undefined | null): boolean =>
  !!id && id.startsWith('local_');

const optionMatchesSearch = (opt: SelectOption, search: string): boolean => {
  if (!search) return true;
  const lower = search.toLowerCase();
  if (opt.label.toLowerCase().includes(lower)) return true;
  return !!opt.extras?.some(
    (e) =>
      e.value.toLowerCase().includes(lower) ||
      e.name.toLowerCase().includes(lower),
  );
};

const DropdownSelect: React.FC<Props> = ({ element }) => {
  const [rawValue, setRawValue] = useElementValue<
    DropdownRichEntry[] | string[] | string
  >(element.id);
  const { document } = useDocument();
  const isReadOnly = useIsReadOnly();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    options?: SelectOption[];
    optionsText?: string;
    defaultValue?: string;
    hidden?: boolean;
    /** Optional: id of a TABLE-type DocumentResponseSet to source options from. */
    responseSetId?: string;
    /** Which column from the response set supplies each row's visible label. */
    labelColumnKey?: string;
    /** When true, switch to multi-select; persisted answer becomes an array. */
    allowMultiple?: boolean;
    /**
     * Multi-select only: minimum picks required to satisfy `required`.
     * configForm number-input writes strings, so accept either shape.
     */
    minimum?: number | string;
    /** Multi-select only: maximum picks the filler may make. */
    maximum?: number | string;
  };
  const label = data?.label || 'Select';
  const subLabel = data?.subLabel;
  const placeholder = data?.placeholder || 'Select an option...';
  const required = data?.required || false;
  const helpText = data?.helpText;
  const allowMultiple = !!data?.allowMultiple;
  // configForm number-inputs persist as strings — coerce, then drop
  // anything that isn't a positive integer.
  const toPositiveInt = (v: unknown): number | null => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  };
  const minimum =
    allowMultiple && required ? toPositiveInt(data?.minimum) : null;
  const maximum = allowMultiple ? toPositiveInt(data?.maximum) : null;
  const richEntries: DropdownRichEntry[] = useMemo(
    () => toRichEntries(rawValue),
    [rawValue],
  );
  // Default-value seeding only makes sense in single-mode; multi-mode
  // skips it so a stale default doesn't pre-fill the array on every
  // render. Plumbed via setRawValue with the rich-entry shape so the
  // saved payload stays consistent.
  const singleValueForDefault = richEntries[0]?.value ?? '';
  const singleValue = useDefaultValue(
    singleValueForDefault,
    (next) => setRawValue(next ? [stringToRichEntry(next)] : []),
    allowMultiple ? undefined : data?.defaultValue,
  );
  void singleValue;
  const selectedValues: string[] = richEntries.map((e) => e.value);
  // Defensive unwrap — early builds of the answer envelope leaked into
  // element.data when the configForm picker wrote responseSetId
  // through `useElementValue`. Newer writes stay bare, but unwrap on
  // read so forms saved during that window keep loading cleanly.
  const unwrapMaybeEnvelope = (v: unknown): string | undefined => {
    if (typeof v === 'string') return v;
    if (
      v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      'value' in (v as Record<string, unknown>) &&
      Object.keys(v as Record<string, unknown>).length === 1
    ) {
      const inner = (v as { value: unknown }).value;
      return typeof inner === 'string' ? inner : undefined;
    }
    return undefined;
  };
  const responseSetId = unwrapMaybeEnvelope(data?.responseSetId);
  const labelColumnKey = unwrapMaybeEnvelope(data?.labelColumnKey);

  // Form-local sets live in the content; the public host inlines the
  // owner's global sets there too (an anonymous filler can't query them).
  const localSet = useMemo(() => {
    if (!responseSetId) return null;
    return (
      (document.responseSets || []).find((s) => s.id === responseSetId) ??
      null
    );
  }, [document.responseSets, responseSetId]);

  const skipGlobal =
    !responseSetId || isLocalResponseSetId(responseSetId) || !!localSet;
  const { data: globalData } = useQuery(DOCUMENT_RESPONSE_SET_QUERY, {
    variables: { id: responseSetId },
    skip: skipGlobal,
  });

  const resolvedSet = localSet || globalData?.documentResponseSet || null;
  const allowUserEntry = !!(
    resolvedSet &&
    ((resolvedSet as { allowUserEntry?: boolean }).allowUserEntry ||
      // MULTI_CHOICE sets (e.g. AI-built option lists) carry free-text via
      // `allowOther` rather than the TABLE set's `allowUserEntry`.
      (resolvedSet as { allowOther?: boolean }).allowOther)
  );
  // Kept for the schema's `userEntryValue` (visible on the set), but
  // no longer used at runtime: user entries land in the array via the
  // "Create '<text>'" combobox affordance below, which writes the
  // typed text itself rather than a configured stand-in.
  void USER_ENTRY_SENTINEL;

  const options = useMemo<SelectOption[]>(() => {
    const set = resolvedSet;
    if (set && (set as { type?: string }).type === 'TABLE') {
      const rows: Array<{ id: string; values: Record<string, unknown> }> =
        (
          set as {
            tableRows?: Array<{ id: string; values: Record<string, unknown> }>;
          }
        ).tableRows || [];
      const cols: Array<{
        key: string;
        name: string;
        showInDropdown?: boolean;
        showColumnNameInDropdown?: boolean;
      }> =
        (
          set as {
            columns?: Array<{
              key: string;
              name: string;
              showInDropdown?: boolean;
              showColumnNameInDropdown?: boolean;
            }>;
          }
        ).columns || [];
      const setLabelKey = (set as { labelColumnKey?: string | null })
        .labelColumnKey;
      const effectiveLabelKey = setLabelKey || labelColumnKey || cols[0]?.key;
      const valueColumnKey = (set as { valueColumnKey?: string | null })
        .valueColumnKey;
      const isScoring = !!(set as { isScoring?: boolean }).isScoring;
      const scoreColumnKey = isScoring
        ? (set as { scoreColumnKey?: string | null }).scoreColumnKey
        : null;
      const flaggedColumnKey = (set as { flaggedColumnKey?: string | null })
        .flaggedColumnKey;
      const extraCols = cols.filter(
        (c) => c.showInDropdown && c.key !== effectiveLabelKey,
      );
      if (rows.length > 0 && effectiveLabelKey) {
        return rows.map((row) => {
          const rawLabel = row.values?.[effectiveLabelKey];
          const text = rawLabel == null ? '' : String(rawLabel);
          let answerValue: string = row.id;
          if (valueColumnKey) {
            const rawValue = row.values?.[valueColumnKey];
            if (rawValue != null && rawValue !== '') {
              answerValue = String(rawValue);
            }
          }
          const extras = extraCols
            .map((col) => {
              const raw = row.values?.[col.key];
              if (raw == null || raw === '') return null;
              return {
                name: col.name,
                value: String(raw),
                showName: col.showColumnNameInDropdown !== false,
              };
            })
            .filter(
              (e): e is { name: string; value: string; showName: boolean } =>
                e !== null,
            );
          let rowScore: number | null = null;
          if (scoreColumnKey) {
            const raw = row.values?.[scoreColumnKey];
            const n = typeof raw === 'number' ? raw : Number(raw);
            rowScore = Number.isFinite(n) ? n : null;
          }
          let rowFlagged: boolean | null = null;
          if (flaggedColumnKey) {
            rowFlagged = isTruthyCell(row.values?.[flaggedColumnKey]);
          }
          return {
            value: answerValue,
            label: text,
            extras: extras.length > 0 ? extras : undefined,
            row: row.values,
            rowScore,
            rowFlagged,
          };
        });
      }
    }

    // Non-TABLE response sets (the MULTI_CHOICE option lists created by the AI
    // builder and the response-set editor) carry their options in `responses`,
    // not `tableRows`. Map them here so these dropdowns render their real
    // options instead of falling through to the placeholder defaults below.
    if (set && Array.isArray((set as { responses?: unknown }).responses)) {
      const isScoring = !!(set as { isScoring?: boolean }).isScoring;
      const responses = (
        set as {
          responses: Array<{
            name?: unknown;
            score?: number | null;
            flagged?: boolean | null;
          }>;
        }
      ).responses;
      const mapped: SelectOption[] = responses
        .map((r) => {
          const name = r?.name == null ? '' : String(r.name);
          return {
            value: name,
            label: name,
            rowScore:
              isScoring &&
              typeof r?.score === 'number' &&
              Number.isFinite(r.score)
                ? r.score
                : null,
            rowFlagged: typeof r?.flagged === 'boolean' ? r.flagged : null,
          };
        })
        .filter((o) => o.label.trim() !== '');
      if (mapped.length > 0) return mapped;
    }

    if (data?.optionsText) {
      return data.optionsText
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => ({ value: line, label: line }));
    }
    return (
      data?.options || [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' },
        { value: 'option3', label: 'Option 3' },
      ]
    );
  }, [resolvedSet, labelColumnKey, data?.optionsText, data?.options]);

  // Map for fast value→option lookup so multi-mode badges can resolve
  // their visible label without a per-render Array.find walk.
  const optionByValue = useMemo(() => {
    const map = new Map<string, SelectOption>();
    for (const o of options) map.set(o.value, o);
    return map;
  }, [options]);

  // Detect a "user entry" answer — any rich entry flagged as `.other`.
  // Reading the boolean directly is more honest than re-deriving from
  // `optionByValue.has(value)`, which gets confused when a user-entry
  // happens to coincide with a row value (e.g. designer set
  // userEntryValue = "OTHER" and a row also has value "OTHER").
  const userEntryText = useMemo<string | null>(() => {
    if (!allowUserEntry) return null;
    const hit = richEntries.find((e) => e.other);
    return hit ? hit.value : null;
  }, [allowUserEntry, richEntries]);
  const isUserEntryPicked = userEntryText !== null;

  // Single-mode trigger text — prefers the snapshotted RichEntry
  // label (so a row deleted from the set after submission still shows
  // its original label) and falls through to the live option if the
  // RichEntry is legacy and didn't carry a label. User-entry answers
  // already store their typed text under `label`.
  const singleTriggerText = !allowMultiple
    ? richEntries[0]?.label ||
      (richEntries[0]
        ? optionByValue.get(richEntries[0].value)?.label || richEntries[0].value
        : '')
    : '';
  // Open: input shows the search query. Closed: single-mode shows the
  // picked label; multi-mode leaves the input empty (badges render to
  // the left of it inside the same control).
  const inputValue = open ? search : allowMultiple ? '' : singleTriggerText;
  const filtered = useMemo(
    () => options.filter((opt) => optionMatchesSearch(opt, search)),
    [options, search],
  );

  // Wipe the search query each time the popover closes so the next open
  // starts clean. Skip on first mount.
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Build a rich entry for a row pick — snapshots the source row +
  // its scoring metadata so the saved answer survives later edits or
  // deletions of the row in the response set.
  const richEntryFromOption = (opt: SelectOption): DropdownRichEntry => ({
    label: opt.label,
    value: opt.value,
    row: opt.row ?? null,
    score: opt.rowScore ?? null,
    flagged: opt.rowFlagged ?? null,
    other: false,
  });

  // Build a rich entry for a user-entered string — pulls the score /
  // flagged config from the set so an "Other" answer can still
  // contribute to scoring + reporting downstream.
  const richEntryFromUserText = (text: string): DropdownRichEntry => {
    const set = resolvedSet as {
      isScoring?: boolean;
      userEntryScore?: number | null;
      userEntryFlagged?: boolean;
    } | null;
    const isScoring = !!set?.isScoring;
    return {
      label: text,
      value: text,
      row: null,
      score: isScoring ? set?.userEntryScore ?? null : null,
      flagged: set?.userEntryFlagged ?? null,
      other: true,
    };
  };

  const pickOption = (next: string) => {
    const option = optionByValue.get(next);
    if (!option) return;
    if (allowMultiple) {
      // Toggle membership without closing — the filler may want to pick
      // more than one option per visit.
      const without = richEntries.filter((e) => e.value !== next);
      const isAdding = without.length === richEntries.length;
      if (isAdding && maximum !== null && richEntries.length >= maximum) {
        // Hit the configured ceiling — silently ignore the add. The
        // chevron-row trigger already shows "Pick up to N options"
        // below as a hint.
        inputRef.current?.focus();
        return;
      }
      const nextSelection = isAdding
        ? [...richEntries, richEntryFromOption(option)]
        : without;
      setRawValue(nextSelection);
      // Keep focus on the input so keyboard search continues to work.
      inputRef.current?.focus();
      return;
    }
    setRawValue([richEntryFromOption(option)]);
    setOpen(false);
    inputRef.current?.blur();
  };

  // Creatable-combobox pattern: typing something that doesn't match
  // any row reveals a "Create '<text>'" item. Picking it commits the
  // typed text as a user entry. In multi-mode each create adds another
  // chip; in single-mode it replaces the current answer.
  const createUserEntry = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const entry = richEntryFromUserText(trimmed);
    if (allowMultiple) {
      // Dedupe — picking "Create foo" twice shouldn't yield two foos.
      if (selectedValues.includes(trimmed)) {
        setSearch('');
        inputRef.current?.focus();
        return;
      }
      if (maximum !== null && richEntries.length >= maximum) {
        // Hit the configured ceiling — drop the keystroke.
        setSearch('');
        inputRef.current?.focus();
        return;
      }
      setRawValue([...richEntries, entry]);
      // Clear the search so the input is ready for the next entry.
      setSearch('');
      inputRef.current?.focus();
      return;
    }
    setRawValue([entry]);
    setOpen(false);
    inputRef.current?.blur();
  };

  // The "Create '<x>'" affordance shows when:
  //   - the set allows user entry
  //   - the search field has non-empty text
  //   - no existing row option's label matches that text exactly
  //   - (multi-mode) the typed text isn't already a chip
  const trimmedSearch = search.trim();
  const exactMatch = trimmedSearch
    ? filtered.find(
        (opt) => opt.label.toLowerCase() === trimmedSearch.toLowerCase(),
      )
    : undefined;
  const canCreate =
    allowUserEntry &&
    trimmedSearch.length > 0 &&
    !exactMatch &&
    !(allowMultiple && selectedValues.includes(trimmedSearch));

  const removeMultiValue = (target: string) => {
    setRawValue(richEntries.filter((e) => e.value !== target));
  };

  const clearAll = () => {
    setRawValue([]);
    setSearch('');
    setOpen(false);
    inputRef.current?.blur();
  };

  // Label resolver for multi-mode badges. Snapshotted RichEntry.label
  // wins so a row deleted from the set after submission still displays
  // the same chip text. Falls back to the bare value when there's
  // nothing better.
  const labelForValue = (v: string): string => {
    const entry = richEntries.find((e) => e.value === v);
    if (entry?.label) return entry.label;
    const option = optionByValue.get(v);
    if (option) return option.label;
    if (allowUserEntry) return v || 'Other';
    return v;
  };

  // Indicator shown alongside the label whenever min and/or max are
  // configured. Live count comes from `richEntries.length` so the
  // filler sees exactly how many more picks they have room for.
  const pickCountIndicator =
    allowMultiple && (minimum !== null || maximum !== null) ? (
      <PickCountBadge
        count={richEntries.length}
        minimum={minimum}
        maximum={maximum}
      />
    ) : null;

  // Read-only (e.g. a completed result): render the answer as static,
  // non-interactive text — no popover, no inputs, no remove/clear controls.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <FieldLabel htmlFor={element.id} required={required}>
              {label}
            </FieldLabel>
            {pickCountIndicator}
          </div>
          <FieldSubLabel>{subLabel}</FieldSubLabel>
        </div>
        {allowMultiple ? (
          selectedValues.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedValues.map((v) => (
                <Badge key={v} variant="secondary" className="font-normal">
                  {labelForValue(v)}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )
        ) : (
          <p className="text-sm">
            {singleTriggerText || (
              <span className="text-muted-foreground">—</span>
            )}
          </p>
        )}
        {helpText && (
          <p className="text-xs text-muted-foreground">{helpText}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <FieldLabel htmlFor={element.id} required={required}>
            {label}
          </FieldLabel>
          {pickCountIndicator}
        </div>
        <FieldSubLabel>{subLabel}</FieldSubLabel>
      </div>
      <Popover
        open={open}
        onOpenChange={(next: boolean) => {
          setOpen(next);
        }}
      >
        <PopoverAnchor asChild>
          {allowMultiple ? (
            // Multi-mode: pill list + an inline search Input wrapped in
            // a single border that visually matches a regular field but
            // grows in height as more chips are added. Clicking the
            // wrapper focuses the input.
            <div
              className="relative flex min-h-9 cursor-text flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 pr-16 text-sm shadow-sm focus-within:outline-none focus-within:ring-1 focus-within:ring-ring"
              onClick={() => inputRef.current?.focus()}
            >
              {selectedValues.map((v) => (
                <Badge
                  key={v}
                  variant="secondary"
                  className="gap-1 pr-1 font-normal"
                >
                  <span>{labelForValue(v)}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${labelForValue(v)}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMultiValue(v);
                    }}
                    className="rounded-sm hover:bg-muted-foreground/20"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <input
                id={element.id}
                ref={inputRef}
                value={inputValue}
                placeholder={
                  selectedValues.length === 0 ? placeholder : undefined
                }
                role="combobox"
                aria-expanded={open}
                autoComplete="off"
                className="min-w-[6rem] flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!open) setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setOpen(false);
                    inputRef.current?.blur();
                  } else if (e.key === 'Enter' && canCreate) {
                    e.preventDefault();
                    createUserEntry(trimmedSearch);
                  } else if (
                    e.key === 'Backspace' &&
                    !search &&
                    selectedValues.length > 0
                  ) {
                    // Quality-of-life: backspace on an empty input
                    // pops the trailing chip — common combobox idiom.
                    removeMultiValue(selectedValues[selectedValues.length - 1]);
                  }
                }}
              />
              {(selectedValues.length > 0 || search) && (
                <button
                  type="button"
                  aria-label="Clear selection"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className="absolute right-8 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
            </div>
          ) : (
            <div className="relative">
              <Input
                id={element.id}
                ref={inputRef}
                value={inputValue}
                placeholder={placeholder}
                role="combobox"
                aria-expanded={open}
                autoComplete="off"
                className="pr-16"
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!open) setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setOpen(false);
                    inputRef.current?.blur();
                  } else if (e.key === 'Enter' && canCreate) {
                    e.preventDefault();
                    createUserEntry(trimmedSearch);
                  }
                }}
              />
              {(singleTriggerText || search) && (
                <button
                  type="button"
                  aria-label="Clear selection"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearAll}
                  className="absolute right-8 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
            </div>
          )}
        </PopoverAnchor>
        <PopoverContent
          // Anchor below the input, match its width, don't steal focus.
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          // Keep keyboard focus on the input so typing stays seamless.
          onOpenAutoFocus={(e: Event) => e.preventDefault()}
          onCloseAutoFocus={(e: Event) => e.preventDefault()}
          // Radix treats the anchor input as outside the popover, so a
          // click or focus event on the input fires onInteractOutside /
          // onFocusOutside and immediately closes the popover — making
          // the option list flash open then snap shut. Suppress when
          // the interaction target is the input itself; outside clicks
          // (anywhere else) still close as expected.
          onInteractOutside={(e: { target: EventTarget | null; preventDefault: () => void }) => {
            if (
              inputRef.current &&
              e.target instanceof Node &&
              inputRef.current.contains(e.target)
            ) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e: { target: EventTarget | null; preventDefault: () => void }) => {
            if (
              inputRef.current &&
              e.target instanceof Node &&
              inputRef.current.contains(e.target)
            ) {
              e.preventDefault();
            }
          }}
        >
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 && !canCreate ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                No matches.
              </p>
            ) : (
              filtered.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    // Suppress the input's blur — onMouseDown fires before
                    // blur, so we can prevent default to keep focus while
                    // we set the value and close.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickOption(option.value)}
                    className={cn(
                      'flex w-full cursor-pointer items-start gap-2 px-3 py-2 text-left text-sm',
                      'hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-muted',
                    )}
                  >
                    <Check
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{option.label}</span>
                      {option.extras?.map((extra) => (
                        <span
                          key={extra.name}
                          className="truncate text-xs text-muted-foreground"
                        >
                          {extra.showName
                            ? `${extra.name}: ${extra.value}`
                            : extra.value}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })
            )}
            {canCreate && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => createUserEntry(trimmedSearch)}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 border-t px-3 py-2 text-left text-sm',
                  'hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span className="text-muted-foreground">Create</span>
                <span className="font-medium">"{trimmedSearch}"</span>
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

/**
 * Pill rendered next to the dropdown's label whenever min and/or max
 * pick counts are configured. Shows the live picked-count and turns
 * destructive when the filler is below the minimum (so they can see at
 * a glance that the field is still under-filled). A maximum on its own
 * stays neutral until they hit the ceiling.
 */
const PickCountBadge: React.FC<{
  count: number;
  minimum: number | null;
  maximum: number | null;
}> = ({ count, minimum, maximum }) => {
  const belowMin = minimum !== null && count < minimum;
  const atMax = maximum !== null && count >= maximum;
  const target =
    minimum !== null && maximum !== null
      ? minimum === maximum
        ? `${minimum}`
        : `${minimum}–${maximum}`
      : minimum !== null
      ? `≥${minimum}`
      : `≤${maximum}`;
  const tone = belowMin
    ? 'border-destructive text-destructive bg-destructive/10'
    : atMax
    ? 'border-amber-500 text-amber-700 bg-amber-50'
    : 'border-border text-muted-foreground bg-muted/40';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium leading-none',
        tone,
      )}
      title={
        minimum !== null && maximum !== null
          ? `Pick ${
              minimum === maximum
                ? `exactly ${minimum}`
                : `between ${minimum} and ${maximum}`
            }`
          : minimum !== null
          ? `Pick at least ${minimum}`
          : `Pick up to ${maximum}`
      }
    >
      {count} / {target}
    </span>
  );
};

export default DropdownSelect;
